import { useState } from "react";
import {
  useContractApprovals,
  useCreateApprovalChain,
  useApproveContract,
  useRejectContract,
} from "@/hooks/useContractApprovals";
import { useTeamMembers } from "@/hooks/useTeam";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Shield, 
  UserCheck, 
  UserX, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Plus,
  Trash2 
} from "lucide-react";
import { format } from "date-fns";

interface ContractApprovalWorkflowProps {
  contractId: string;
  contractStatus: string;
  approvalRequired?: boolean;
  approvalStatus?: string;
}

const statusConfig = {
  pending: { icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10", label: "Pending" },
  approved: { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10", label: "Approved" },
  rejected: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10", label: "Rejected" },
};

export function ContractApprovalWorkflow({ 
  contractId, 
  contractStatus,
  approvalRequired = false,
  approvalStatus = "none",
}: ContractApprovalWorkflowProps) {
  const { data: approvals, isLoading } = useContractApprovals(contractId);
  const { data: teamMembers } = useTeamMembers();
  const createApprovalChain = useCreateApprovalChain();
  const approveContract = useApproveContract();
  const rejectContract = useRejectContract();

  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [approvers, setApprovers] = useState<{ userId: string; level: number }[]>([]);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject">("approve");
  const [selectedApproval, setSelectedApproval] = useState<string | null>(null);
  const [comments, setComments] = useState("");

  const addApprover = () => {
    const nextLevel = approvers.length + 1;
    setApprovers([...approvers, { userId: "", level: nextLevel }]);
  };

  const removeApprover = (index: number) => {
    const updated = approvers.filter((_, i) => i !== index);
    // Resequence levels
    setApprovers(updated.map((a, i) => ({ ...a, level: i + 1 })));
  };

  const updateApprover = (index: number, userId: string) => {
    const updated = [...approvers];
    updated[index].userId = userId;
    setApprovers(updated);
  };

  const handleCreateChain = () => {
    const validApprovers = approvers.filter(a => a.userId);
    if (validApprovers.length === 0) return;
    
    createApprovalChain.mutate(
      { contractId, approvers: validApprovers },
      {
        onSuccess: () => {
          setSetupDialogOpen(false);
          setApprovers([]);
        },
      }
    );
  };

  const handleAction = () => {
    if (!selectedApproval) return;
    
    if (actionType === "approve") {
      approveContract.mutate(
        { approvalId: selectedApproval, contractId, comments: comments || undefined },
        { onSuccess: () => { setActionDialogOpen(false); setComments(""); } }
      );
    } else {
      if (!comments) return;
      rejectContract.mutate(
        { approvalId: selectedApproval, contractId, comments },
        { onSuccess: () => { setActionDialogOpen(false); setComments(""); } }
      );
    }
  };

  const openActionDialog = (approvalId: string, type: "approve" | "reject") => {
    setSelectedApproval(approvalId);
    setActionType(type);
    setComments("");
    setActionDialogOpen(true);
  };

  if (isLoading) {
    return <Skeleton className="h-40" />;
  }

  // Show setup button if no approvals exist and contract is draft
  if (!approvalRequired && contractStatus === "draft") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4" />
            Approval Workflow
          </CardTitle>
          <CardDescription>
            Require approvals before this contract can be sent
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                Setup Approval Chain
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Setup Approval Chain</DialogTitle>
                <DialogDescription>
                  Add approvers in order. Each level must approve before the next.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {approvers.map((approver, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Badge variant="outline" className="shrink-0">
                      Level {approver.level}
                    </Badge>
                    <Select
                      value={approver.userId}
                      onValueChange={(value) => updateApprover(index, value)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select approver..." />
                      </SelectTrigger>
                      <SelectContent>
                        {teamMembers?.map((member) => (
                          <SelectItem 
                            key={member.id} 
                            value={member.id}
                            disabled={approvers.some(a => a.userId === member.id)}
                          >
                            {member.first_name} {member.last_name} ({member.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeApprover(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" className="w-full gap-2" onClick={addApprover}>
                  <Plus className="h-4 w-4" />
                  Add Approver
                </Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSetupDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateChain}
                  disabled={approvers.filter(a => a.userId).length === 0 || createApprovalChain.isPending}
                >
                  Create Approval Chain
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  // Show approval status
  if (!approvals || approvals.length === 0) {
    return null;
  }

  const overallStatus = statusConfig[approvalStatus as keyof typeof statusConfig] || statusConfig.pending;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="h-4 w-4" />
                Approval Workflow
              </CardTitle>
              <CardDescription>
                {approvals.length} approver{approvals.length !== 1 ? 's' : ''} in chain
              </CardDescription>
            </div>
            <Badge className={`${overallStatus.bg} ${overallStatus.color} border-0`}>
              {overallStatus.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {approvals.map((approval, index) => {
              const config = statusConfig[approval.status];
              const StatusIcon = config.icon;
              const profile = approval.profiles;
              const approverName = profile 
                ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email
                : 'Unknown';

              return (
                <div
                  key={approval.id}
                  className={`relative flex items-start gap-3 p-3 rounded-lg ${config.bg}`}
                >
                  {index < approvals.length - 1 && (
                    <div className="absolute left-[22px] top-[44px] w-0.5 h-[calc(100%-20px)] bg-border" />
                  )}
                  
                  <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${config.bg} border-2 border-current ${config.color}`}>
                    <StatusIcon className="h-4 w-4" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">
                        Level {approval.approval_level}: {approverName}
                      </span>
                      <Badge variant="outline" className={`text-xs ${config.color}`}>
                        {config.label}
                      </Badge>
                    </div>
                    
                    {approval.comments && (
                      <p className="text-xs text-muted-foreground mb-1">
                        "{approval.comments}"
                      </p>
                    )}
                    
                    {approval.approved_at && (
                      <p className="text-xs text-muted-foreground">
                        Approved on {format(new Date(approval.approved_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    )}
                    
                    {approval.rejected_at && (
                      <p className="text-xs text-muted-foreground">
                        Rejected on {format(new Date(approval.rejected_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    )}
                    
                    {approval.status === "pending" && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => openActionDialog(approval.id, "approve")}
                        >
                          <UserCheck className="h-3 w-3" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openActionDialog(approval.id, "reject")}
                        >
                          <UserX className="h-3 w-3" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "Approve Contract" : "Reject Contract"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve" 
                ? "Add optional comments for your approval."
                : "Please provide a reason for rejection."
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Comments {actionType === "reject" && <span className="text-destructive">*</span>}</Label>
              <Textarea
                placeholder={actionType === "approve" ? "Optional comments..." : "Reason for rejection..."}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={actionType === "reject" && !comments}
              variant={actionType === "approve" ? "default" : "destructive"}
            >
              {actionType === "approve" ? (
                <>
                  <UserCheck className="h-4 w-4 mr-2" />
                  Approve
                </>
              ) : (
                <>
                  <UserX className="h-4 w-4 mr-2" />
                  Reject
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
